// AdminPanel.js - Admin-only panel for administrative actions
import React, { useState } from "react";
import { disconnectAllEmployees } from "../utils/employeesApi";
import { getCurrentUserEmail, getCurrentUserRole } from "../utils/auth";
import { logEmployeeActivity } from "../utils/logsApi";
import Modal from "./Modal";
import "../App.css";

const AdminPanel = () => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const openModal = (title, message) => {
    setModal({ isOpen: true, title, message });
  };

  const closeModal = () => {
    setModal({ isOpen: false, title: "", message: "" });
  };

  const handleDisconnectAll = async () => {
    const adminEmail = getCurrentUserEmail();
    const adminRole = getCurrentUserRole();

    // Double-check admin role
    if (adminRole !== 2) {
      openModal("Error", "Only administrators (role 2) can perform this action.");
      return;
    }

    // Confirm action
    const confirmed = window.confirm(
      "⚠️ WARNING: This will disconnect ALL users except you.\n\n" +
      "All other employees will be logged out immediately.\n\n" +
      "Are you sure you want to continue?"
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      const result = await disconnectAllEmployees(adminEmail);

      if (result.success) {
        const disconnectedCount = result.data?.disconnectedCount || 0;
        openModal(
          "Success",
          `Successfully disconnected ${disconnectedCount} user(s).\n\nAll other employees have been logged out.`
        );

        // Log admin action
        await logEmployeeActivity(
          "ADMIN_DISCONNECT_ALL",
          adminEmail,
          adminRole,
          `Admin disconnected all employees. Count: ${disconnectedCount}`
        );
      } else {
        openModal("Error", result.message || "Failed to disconnect employees.");
      }
    } catch (error) {
      console.error("Error disconnecting employees:", error);
      openModal("Error", "An error occurred while disconnecting employees.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <h2 className="admin-panel-title">Admin Panel</h2>
      <div className="admin-panel-content">
        <div className="admin-action-card">
          <h3>Disconnect All Users</h3>
          <p>
            This action will immediately log out all employees except you (the admin).
            They will need to log in again with their credentials.
          </p>
          <p className="admin-warning">
            ⚠️ Use this feature carefully. All active sessions will be terminated.
          </p>
          <button
            className="admin-disconnect-button"
            onClick={handleDisconnectAll}
            disabled={loading}
          >
            {loading ? "Disconnecting..." : "Disconnect All Users"}
          </button>
        </div>
      </div>
      <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        onClose={closeModal}
      />
    </div>
  );
};

export default AdminPanel;

